import { MODULE_REGISTRY } from '@hetivo/hetivo-lib-types';
import type { ModuleAreaKey, TenantProfile } from '@hetivo/hetivo-lib-types';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useAuth } from '&/auth';
import { useTenantProfileQueryOptions } from '&/api/tenant-profile';

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Reusable, read-only entitlement primitive for the front office.
 *
 * `enabledModules` are the sellable AREA keys the tenant owns; `hasModule` layers the registry's
 * `alwaysIncluded` flag on top (Fleet is always true, never stored). `isReady` mirrors the
 * `permissions.isReady` idiom: it is false until the tenant profile query has resolved
 * SUCCESSFULLY, so a route guard denies an entitled tenant neither while entitlement is loading nor
 * when the request failed.
 */
export interface TenantModulesResult {
  enabledModules: ModuleAreaKey[];
  hasModule: (areaKey: ModuleAreaKey) => boolean;
  /** The profile lookup failed: entitlement is UNKNOWN (not "none"). Lets a gate fall through
   * instead of waiting forever; the server-side guard still enforces. */
  isError: boolean;
  isReady: boolean;
}

// ─── Pure helper ────────────────────────────────────────────────────────────────

const ALWAYS_INCLUDED_AREAS = new Set<ModuleAreaKey>(
  MODULE_REGISTRY.filter((area) => area.alwaysIncluded).map((area) => area.key),
);

/**
 * Pure entitlement check: an `alwaysIncluded` area (Fleet) is always true; a sellable area is true
 * iff it is present in the tenant's `enabledModules`. Matches the backend/registry semantics.
 */
export function computeHasModule(enabledModules: ModuleAreaKey[], areaKey: ModuleAreaKey): boolean {
  return ALWAYS_INCLUDED_AREAS.has(areaKey) || enabledModules.includes(areaKey);
}

/** react-query `select` narrowing the cached profile to its `enabledModules` — hoisted so it is a stable
 * reference across renders (an inline arrow would re-run the selector every render). */
const selectEnabledModules = (profile: TenantProfile): ModuleAreaKey[] => profile.enabledModules;

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Reads the tenant's entitled modules from `GET /tenants/me` (the shared `useTenantProfileQueryOptions`,
 * so it reuses `useTenantProfile`'s cache entry and fires no extra request) and exposes a race-safe
 * `hasModule` predicate plus an `isReady` gate. A react-query `select` narrows the cached profile to its
 * `enabledModules`, so this hook only ever re-renders on a module change.
 *
 * The query is gated on `isAuthenticated` (as the permissions provider is) so it never fires on the
 * public/login surfaces where there is no token — an ungated fetch would 401 and trigger sign-out.
 *
 * `isReady` keys off `isSuccess`, NOT `isFetched`: `isFetched` also turns true after a FAILED
 * attempt, and since `data` is then undefined `enabledModules` falls back to `[]` — so a rejected
 * `/tenants/me` would look exactly like a confirmed empty entitlement and 404 an entitled tenant's
 * `/settings/maintenance`. Only a confirmed success may be treated as absent; a failure leaves
 * `isReady` false, the route guard short-circuits, and the error surfaces through the page's own
 * query state. (PR #135 review.)
 */
export function useTenantModules(): TenantModulesResult {
  const { isAuthenticated } = useAuth();

  const { data, isError, isSuccess } = useQuery({
    ...useTenantProfileQueryOptions(),
    enabled: isAuthenticated,
    select: selectEnabledModules,
  });

  const enabledModules = useMemo<ModuleAreaKey[]>(() => (isAuthenticated ? (data ?? []) : []), [isAuthenticated, data]);

  const hasModule = useCallback(
    (areaKey: ModuleAreaKey): boolean => computeHasModule(enabledModules, areaKey),
    [enabledModules],
  );

  return useMemo<TenantModulesResult>(
    () => ({ enabledModules, hasModule, isError: isAuthenticated && isError, isReady: isAuthenticated && isSuccess }),
    [enabledModules, hasModule, isAuthenticated, isError, isSuccess],
  );
}
